import {
  init,
  type BrowserOptions,
  tanstackRouterBrowserTracingIntegration,
  browserTracingIntegration,
  httpClientIntegration,
  replayIntegration,
  consoleLoggingIntegration,
  thirdPartyErrorFilterIntegration,
  extraErrorDataIntegration,
  makeBrowserOfflineTransport,
} from "@sentry/react";
import type { AnyRouter } from "@tanstack/react-router";

import { basepathAwareRouter } from "./basepath-aware-router";
import {
  ConsentManager,
  createConsentManagerTransport,
} from "./consent-manager";
import { env } from "./env";
import { getFetchFailureFingerprint } from "./fetch-failure-fingerprint";
import { FIRST_PARTY_REQUEST_TARGETS } from "./first-party-request-targets";
import {
  getHttpClientFingerprint,
  hasUnresolvedPathSegment,
  isHttpClientEvent,
} from "./http-client-fingerprint";
import { getQueryStatusAssertionFingerprint } from "./query-status-assertion-fingerprint";
import { scrubSensitiveData } from "./scrub-sensitive-data";
import { isSessionLostEvent } from "./session-lost-event";
import { isSuppressedApiError } from "./suppressed-api-error";
import { USER_VERDICT_STATUSES } from "./user-verdict-statuses";

function getTags() {
  const { pr } =
    window.location.hostname.match(/^(?<pr>pr-[_0-9]+)\./)?.groups ?? {};
  return {
    pr,
    whoami: env.whoami,
    revision: import.meta.env.VITE_AWS_COMMIT_ID,
  };
}

function getSampleRate() {
  const lsSampleRate = localStorage.getItem("sentry_sample_rate");
  if (lsSampleRate && !Number.isNaN(+lsSampleRate)) {
    return +lsSampleRate;
  } else if (lsSampleRate) {
    localStorage.removeItem("sentry_sample_rate");
  }
  return env.sampleRate ?? 1;
}

const PRODUCTION_ENVIRONMENT = "prod";

const COGNITO_URL_PATTERN =
  /^https:\/\/cognito-(idp|identity)\.[a-z0-9-]+\.amazonaws\.com\//;

function isIgnoredHostname(url: string): boolean {
  const { hostname } = new URL(url);
  return env.IGNORED_HOSTNAME_SUFFIXES.some((suffix) =>
    hostname.endsWith(suffix),
  );
}

function getResponseStatusCode(event: {
  contexts?: { response?: unknown };
}): number | null {
  const response = event.contexts?.response;
  if (
    typeof response === "object" &&
    response !== null &&
    "status_code" in response &&
    typeof response.status_code === "number"
  ) {
    return response.status_code;
  }
  return null;
}

export type InitSentryOptions = Pick<
  BrowserOptions,
  "dsn" | "environment" | "tracePropagationTargets"
> & {
  consentManager?: ConsentManager;
  router?: AnyRouter;
};

export function initSentry({
  dsn,
  environment = "local",
  consentManager = new ConsentManager({
    isConsenting: false,
    awaitForConsent: false,
    isConsentRequired: false,
  }),
  router,
  tracePropagationTargets,
}: InitSentryOptions = {}): void | BrowserOptions {
  if (environment === "local") {
    return;
  }

  const sampleRate = getSampleRate();
  // Replays are only worth their quota on the environment real users hit; the
  // branch/PR/preprod deployments would burn it on our own browsing.
  const isProduction = environment === PRODUCTION_ENVIRONMENT;

  const browserTracing = router
    ? tanstackRouterBrowserTracingIntegration(basepathAwareRouter(router))
    : browserTracingIntegration();

  consentManager.setEnvironment(environment);

  const sentryOptions: BrowserOptions = {
    dsn,
    sampleRate,
    environment,
    release: env.release,
    tracesSampleRate: sampleRate,
    replaysSessionSampleRate: isProduction ? 0.01 : 0,
    replaysOnErrorSampleRate: isProduction ? 1 : 0,
    normalizeDepth: 6,
    attachStacktrace: true,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      /^AbortError/,
      /Failed to fetch dynamically imported module/,
      /Importing a module script failed/,
      /Loading chunk [\d]+ failed/,
      "Non-Error promise rejection captured",
    ],
    // Errors thrown from third-party scripts we don't control. The hostname
    // filter in beforeSend only looks at the page URL, so script-frame errors
    // (e.g. the Jimo widget crashing on its own state) must be dropped here.
    denyUrls: [/jimo-main\.js/, /\.usejimo\.com/],
    ...(tracePropagationTargets ? { tracePropagationTargets } : {}),
    integrations: [
      browserTracing,
      // 401 is an expected auth failure; report the rest of the 4xx range.
      // User-verdict statuses are captured here and narrowed in beforeSend,
      // so the integration stays the single place that decides which
      // statuses are looked at.
      httpClientIntegration({
        failedRequestStatusCodes: [
          [400, 400],
          [402, 499],
        ],
        failedRequestTargets: FIRST_PARTY_REQUEST_TARGETS,
      }),
      // default masking replaces every text node with "*** ****", which makes
      // replays unusable. Text and media are readable; inputs stay masked and
      // anything sensitive can opt back in with data-sentry-mask.
      ...(isProduction
        ? [
            replayIntegration({
              maskAllText: false,
              blockAllMedia: false,
              mask: ["[data-sentry-mask]"],
              block: ["[data-sentry-block]"],
            }),
          ]
        : []),
      consoleLoggingIntegration({ levels: ["warn", "error"] }),
      // surfaces non-standard error props (e.g. AxiosError config/code/status)
      extraErrorDataIntegration({ depth: 6 }),
      thirdPartyErrorFilterIntegration({
        filterKeys: ["mobsuccess-front"],
        behaviour: "apply-tag-if-contains-third-party-frames",
      }),
    ],
    initialScope: { tags: getTags() },
    transport: makeBrowserOfflineTransport(
      createConsentManagerTransport(consentManager),
    ),
    beforeSend(event) {
      const requestUrl = event.request?.url;
      if (requestUrl && isIgnoredHostname(requestUrl)) {
        return null;
      }
      // Cognito answers 400 on bogus username/password combinations
      if (
        requestUrl &&
        COGNITO_URL_PATTERN.test(requestUrl) &&
        getResponseStatusCode(event) === 400
      ) {
        return null;
      }
      // A first-party user-verdict 4xx (validation, conflict, bad upload,
      // locked entity — see user-verdict-statuses.ts) is the backend rejecting
      // what the user did, and the UI shows it. Only the requests built from
      // an unresolved parameter are ours to fix, so those are the only ones
      // kept.
      {
        const statusCode = getResponseStatusCode(event);
        if (
          statusCode !== null &&
          USER_VERDICT_STATUSES.has(statusCode) &&
          isHttpClientEvent(event) &&
          requestUrl &&
          !hasUnresolvedPathSegment(requestUrl)
        ) {
          return null;
        }
      }
      // Same verdict statuses when the axios error escapes a query or a
      // loader instead, plus owned-backend 5xx: those backends report their
      // own failures to their own Sentry project, the frontend copy is a
      // duplicate of a bug the frontend cannot fix.
      if (isSuppressedApiError(event)) {
        return null;
      }
      // An expired session is not a defect: the page hands off to sign-in.
      // Filtered here rather than at the error boundary so that direct
      // captureException calls on a failed Amplify call are covered too
      // (PLATFORM-LCM-5P4).
      if (isSessionLostEvent(event)) {
        return null;
      }
      const fingerprint =
        getHttpClientFingerprint(event) ??
        getQueryStatusAssertionFingerprint(event) ??
        getFetchFailureFingerprint(event);
      if (fingerprint) {
        event.fingerprint = fingerprint;
      }
      return scrubSensitiveData(event);
    },
  };

  init(sentryOptions);

  return sentryOptions;
}

export { ConsentManager };
export { captureSupportLog, type SupportLog } from "./capture-support-log";
export { reactErrorHandler, withScope } from "@sentry/react";
